/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Route-level permission guard.
 *
 * Wraps page content and checks if the user has the required permission(s).
 * If not, redirects to home or renders a fallback "No Access" message.
 *
 * Usage in App.tsx:
 *   <Route element={<RequirePermission code="submissions:view" />}>
 *     <Route path="/submissions" element={<SubmissionsPage />} />
 *   </Route>
 *
 * Or with any-of check:
 *   <RequirePermission codes={["admin:manage_users", "admin:view_reference"]}>
 *     <AdminPage />
 *   </RequirePermission>
 */

import { Loader2, Lock } from "lucide-react";
import { Outlet } from "react-router-dom";
import { usePermissions } from "@/context/PermissionContext";

interface RequirePermissionProps {
  /** Single permission code required */
  code?: string;
  /** Any of these permission codes grants access */
  codes?: string[];
  /**
   * Exact role required (e.g. "system_admin"). Unlike `code`/`codes`, a role gate is NOT bypassed
   * by super-user — it restricts a page to that one role only. Takes precedence over code/codes.
   */
  role?: string;
  /** Optional children (if not using as Route element with Outlet) */
  children?: React.ReactNode;
  /** Custom fallback when access denied (default: redirect message) */
  fallback?: React.ReactNode;
}

function NoAccess() {
  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
      <Lock className="h-12 w-12 text-muted-foreground" />
      <h2 className="text-xl font-semibold">Access Denied</h2>
      <p className="text-muted-foreground">You do not have permission to view this page.</p>
      <p className="text-sm text-muted-foreground">
        Contact your administrator if you believe this is an error.
      </p>
    </div>
  );
}

export function RequirePermission({
  code,
  codes,
  role,
  children,
  fallback,
}: RequirePermissionProps) {
  const { can, canAny, hasRole, isLoaded } = usePermissions();

  // Still loading permissions — show spinner to avoid blank flash
  if (!isLoaded) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAccess = role ? hasRole(role) : code ? can(code) : codes ? canAny(...codes) : true;

  if (!hasAccess) {
    return <>{fallback ?? <NoAccess />}</>;
  }

  // If used as a Route element, render Outlet for child routes
  // If used as a wrapper, render children
  return children ? <>{children}</> : <Outlet />;
}
